import React, { useEffect } from 'react';
import { Formik } from 'formik';
import SmallModal from 'src/components/small-modal/SmallModal';
import LiteralsService from 'src/services/literals/LiteralsService';
import layoutStyles from 'src/styles/layout.module.scss';
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import * as Yup from 'yup';
import { useCreateCategory } from 'src/hooks/queries/categories';
import { CustomSubcategoryModalViewModel } from 'src/types/view-models/transactions/custom-subcategory-modal.view.model';
import { CategoryModel } from 'src/types/category.model';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import ObjectUtils from 'src/utils/object';
import { useSelectDetailedContext } from 'src/hooks/useSelectDetailedContext';

/** Form validation schema */
const validationSchema = (existingCategoryNames: string[]): Yup.AnySchema =>
    Yup.object({
        customSubcategoryName: Yup.string()
            .required(LiteralsService.getLiteral('CustomSubcategoryModal.required'))
            .test(
                'caseInsensitiveExistsCheck',
                LiteralsService.getLiteral('CustomSubcategoryModal.alreadyExists'),
                (value: string) => !ObjectUtils.caseInsensitiveIncludes(existingCategoryNames, value)
            )
    });

export default function CustomSubcategoryModal({
    categories,
    parentCategoryId,
    closeModal
}: CustomSubcategoryModalViewModel): JSX.Element {
    const { mutateAsync: createCategory } = useCreateCategory();
    const { setPreventClose } = useSelectDetailedContext();
    const { pathname } = useLocation();

    useEffect(() => {
        trackClick(pathname, adobeTags.Transactions.Row.CustomSubcategory);
        setPreventClose(true);
    }, []);

    const handleSubmit = async (values: { customSubcategoryName: string }): Promise<void> => {
        trackClick(pathname, adobeTags.Transactions.SubcategoryModal.Apply);
        const newCustomSubcategory: CategoryModel = await createCategory([
            values.customSubcategoryName,
            parentCategoryId
        ]);
        void closeModal(newCustomSubcategory);
        setPreventClose(false);
    };

    return (
        <Formik
            initialValues={{ customSubcategoryName: '' }}
            validationSchema={validationSchema(categories?.map((c: CategoryModel) => c.name))}
            onSubmit={handleSubmit}
        >
            {({ submitForm }) => (
                <SmallModal
                    onClose={async () => {
                        await closeModal();
                        trackClick(pathname, adobeTags.Transactions.SubcategoryModal.Cancel);
                        setPreventClose(false);
                    }}
                    headerText={LiteralsService.getLiteral('CustomSubcategoryModal.header')}
                    submitButtonText={LiteralsService.getLiteral('CommonText.apply')}
                    onSubmit={submitForm}
                    data-cy={Selectors.Transactions.CUSTOM_SUBCATEGORY_MODAL}
                    id="customSubcategoryModal"
                >
                    <div className={layoutStyles.drawerSection}>
                        <BasicInput
                            id="customSubcategoryName"
                            label={LiteralsService.getLiteral('CustomSubcategoryModal.name')}
                            onClick={() => trackClick(pathname, adobeTags.Transactions.SubcategoryModal.Name)}
                        />
                    </div>
                </SmallModal>
            )}
        </Formik>
    );
}
